<template>
    <div class="webshop-account">
        <grid
            :fullGrid="true"
            :passedData="accountData"
            identificator="id"
            :columns="accountColumns"
            :passedCount="accountDataCount"
            :passedPageSize="9999"
            collapsible
            :collapsedIndex="collapsedIndex"
            @trClicked="accountClickHandler"
            :pagination="false"
        >
            <div class="inner-content">
                <grid
                    fullGrid
                    :passedData="cardsData"
                    identificator="id"
                    :columns="cardColumns"
                    :pagination="false"
                />
                <ul>
                    <li>
                        <span class="desc">{{ $t('WEBSHOPS__DIRECT_PAYINS') }}</span>
                        <span class="value">{{ additionalInfo.directPayins + ' ' + webshopCurrency }}</span>
                    </li>
                    <li>
                        <span class="desc">{{ $t('WEBSHOPS__DIRECT_DISBURSEMENTS') }}</span>
                        <span class="value">{{ additionalInfo.directDisbursements + ' ' + webshopCurrency}}</span>
                    </li>
                    <li>
                        <span class="desc">{{ $t('WEBSHOPS__CONFIRMED_DISBURSEMENTS') }}</span>
                        <span class="value">{{ additionalInfo.confirmedDisbursements + ' ' + webshopCurrency }}</span>
                    </li>
                    <li>
                        <span class="desc">{{ $t('WEBSHOPS__BALANCE') }}</span>
                        <span class="value">{{ additionalInfo.balance + ' ' + webshopCurrency}}</span>
                    </li>
                    <li>
                        <span class="desc">{{ $t('WEBSHOPS__REVENUES_IN_ACCOUNT_PERIOD') }}</span>
                        <span class="value">{{ additionalInfo.revenues + ' ' + webshopCurrency}}</span>
                    </li>
                </ul>
                <div class="actions" v-show="!selectedAccountID" v-if="isSupervisor">
                    <switcher v-model="clearBalance" :title="$t('WEBSHOPS__CLEAR_BALANCE')" />
                    <button class="btn" @click="save">{{ $t('WEBSHOPS__SAVE') }}</button>
                </div>
            </div>
        </grid>
    </div>
</template>

<script>
import Grid from '@/components/ReusableComponents/Grid/Grid.vue'
import Switcher from '@/components/ReusableComponents/Switcher/Switcher.vue'
import axios from '@/Helpers/AxiosInstance.js'
import { addHours, getDateString } from '@/Helpers/HelperFunctions.js'
export default {
    components: {
        Grid,
        Switcher,
    },
    created() {
        this.getWebshopAccount()
    },
    props: {
        webshopID: Number,
        webshopCurrency: String,
    },
    data() {
        return {
            accountData: [],
            accountDataCount: 0,
            accountColumns: [
                {
                    title: 'ID',
                    key: 'myID',
                },
                {
                    title: this.$t('WEBSHOPS__FROM_DATE'),
                    key: 'fromDate',
                    customTemplate: (item, val) =>
                        val ? addHours(val, 2) : '-',
                },
                {
                    title: this.$t('WEBSHOPS__TO_DATE'),
                    key: 'toDate',
                    customTemplate: (item, val) => {
                        if(item.raw) {
                            return val ? getDateString(val, 'yyyy-MM-dd HH:mm') : '-'
                        }
                        return val ? addHours(val, 2) : '-'
                    }
                },
                {
                    title: this.$t('WEBSHOPS__REVENUE'),
                    key: 'revenueDecimal',
                    customTemplate: (item, val) => val + ' ' + this.webshopCurrency                    
                },
            ],
            selectedAccountID: null,
            collapsedIndex: 0,
            cardsData: [],
            cardColumns: [
                {
                    title: this.$t('WEBSHOPS__TYPE'),
                    key: 'type',
                },
                {
                    title: this.$t('WEBSHOPS__BALANCE'),
                    key: 'balanceDecimal',
                    customTemplate: (item, val) => val + ' ' + this.webshopCurrency
                },
                {
                    title: this.$t('WEBSHOPS__BEGIN'),
                    key: 'begin',
                    customTemplate: (item, val) => val
                },
                {
                    title: this.$t('WEBSHOPS__NEW'),
                    key: 'new',
                    customTemplate: (item, val) => val
                },
                {
                    title: this.$t('WEBSHOPS__ACTIVATE_COUNT'),
                    key: 'activatedCount',
                },
                {
                    title: this.$t('WEBSHOPS__END'),
                    key: 'end',
                    customTemplate: (item, val) => val
                },
            ],
            mainGridResponse: null,
            additionalInfo: {
                directPayins: '',
                directDisbursements: '',
                confirmedDisbursements: '',
                balance: '',
                revenues: '',
            },
            clearBalance: false,
        }
    },
    methods: {
        getWebshopAccount() {
            axios
                .get('WebShop/GetWebShopAccount', {
                    params: {
                        WebShopId: this.webshopID,
                        Save: false,
                        ClearBalance: false,
                    },
                })
                .then(resp => {
                    this.processAndSetMainGridData(resp.data.response)
                })
        },
        accountClickHandler(element) {
            this.selectedAccountID = element.id
            var index = this.accountData.indexOf(
                this.accountData.filter(c => c.id == element.id)[0]
            )
            this.collapsedIndex =
                index + 1 == this.collapsedIndex ? 0 : index + 1
            if (!this.collapsedIndex) return
            if (this.selectedAccountID) {
                axios
                    .get('WebShop/GetWebShopAccount', {
                        params: {
                            WebShopId: this.webshopID,
                            WebShopAccountId: this.selectedAccountID,
                            Save: false,
                            ClearBalance: false,
                        },
                    })
                    .then(resp => {
                        let response = resp.data.response
                        this.generateCardData(response)
                        this.displayAdditionalInfo(response)
                    })
            } else {
                this.generateCardData(this.mainGridResponse)
                this.displayAdditionalInfo(this.mainGridResponse)
            }
        },
        generateCardData(response) {
            var accountCards = response.webShopAccountCards
            for (let key in accountCards) {
                accountCards[key]['type'] = 'Account Card'
            }
            var prepaidCards = response.webShopAccountPrepaidCards
            for (let key in prepaidCards) {
                prepaidCards[key]['type'] = 'Prepaid Card'
                prepaidCards[key]['balanceDecimal'] =
                    prepaidCards[key]['valueDecimal']
            }
            this.cardsData = accountCards.concat(prepaidCards)
        },
        displayAdditionalInfo(response) {
            this.additionalInfo.directPayins =
                response.directs.directPayinDecimal
            this.additionalInfo.directDisbursements =
                response.directs.directDisbursementDecimal
            this.additionalInfo.confirmedDisbursements =
                response.confirmedDisbursement.confirmedDisbursementsDecimal
            this.additionalInfo.balance = response.newBalanceDecimal
            this.additionalInfo.revenues = response.revenuesDecimal
        },
        save() {
            axios
                .get('WebShop/GetWebShopAccount', {
                    params: {
                        WebShopId: this.webshopID,
                        Save: true,
                        ClearBalance: this.clearBalance,
                    },
                })
                .then(resp => {
                    this.collapsedIndex = 0
                    this.processAndSetMainGridData(resp.data.response)
                })
        },
        processAndSetMainGridData(response) {
            this.mainGridResponse = response
            let accounts = response.webShopAccount.sort((a, b) => b.id - a.id)
            accounts.unshift({
                fromDate: null,
                toDate: new Date(),
                revenueDecimal: response.revenuesDecimal,
                id: 0,
                raw: true,
            })
            accounts
                .sort((a,b) => {
                    let atimestamp = new Date(a.toDate).getTime()
                    let btimestamp = new Date(b.toDate).getTime()
                    return atimestamp - btimestamp
                })
            accounts.forEach((el, index) => el.myID = index)
            accounts.sort((a,b) => {
                    return b.myID - a.myID
                })
            this.accountData = accounts
        },
    },
    computed: {
        isSupervisor() {
            return this.$store.state.shop
        },
    },
    watch: {
        webshopID() {
            this.getWebshopAccount()
        },
    },
}
</script>

<style lang="scss" scoped>
.webshop-account {
    margin-top: 30 * $rem;
}

.webshop-account /deep/ .grid {
    tr td {
        cursor: pointer;
    }
}

.inner-content {
    /deep/ .grid tr td {
        cursor: initial;
    }

    ul {
        list-style: none;
        margin: 30 * $rem 0;

        li {
            padding: 5 * $rem 0;
            font-size: 16 * $rem;
            text-align: left;

            .desc {
                width: 22rem;
                display: inline-block;
            }

            .value {
                font-weight: 700;
            }
        }
    }

    .actions {
        display: flex;

        .btn {
            margin-left: 15 * $rem;
        }
    }
}
</style>